import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function LocationGallery({ isOpen, onClose, location }) {
  if (!isOpen) return null;

  const galleries = {
    'Pacific Coast': [
      { url: 'https://images.unsplash.com/photo-1662044069225-ab6383ba03bd?w=1200&q=80', caption: 'Puerto Vallarta Beach' },
      { url: 'https://images.unsplash.com/photo-1645556655631-9764270324fa?w=1200&q=80', caption: 'Coastal Aerial View' },
      { url: 'https://images.unsplash.com/photo-1512813498716-4770e7d2d66c?w=1200&q=80', caption: 'Tropical Beach' },
      { url: 'https://images.unsplash.com/photo-1583037189850-1921ae7c6c22?w=1200&q=80', caption: 'Rocky Coastline' }
    ],
    'Colonial Towns': [
      { url: 'https://images.unsplash.com/photo-1709065130843-7d68eff4c0b8?w=1200&q=80', caption: 'Guadalajara Plaza' },
      { url: 'https://images.unsplash.com/photo-1703220347575-2c5218ad384d?w=1200&q=80', caption: 'Colonial Architecture' },
      { url: 'https://images.unsplash.com/photo-1518638150340-f706e86654de?w=1200&q=80', caption: 'Historic Streets' },
      { url: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=1200&q=80', caption: 'Town Square' }
    ],
    'Mountain Forests': [
      { url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80', caption: 'Pine Forest' },
      { url: 'https://images.unsplash.com/photo-1675095169234-f645a6776f95?w=1200&q=80', caption: 'Mountain Vista' },
      { url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', caption: 'Forest Trail' },
      { url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1200&q=80', caption: 'Misty Mountains' }
    ],
    'Canyons & Desert': [
      { url: 'https://images.unsplash.com/photo-1666198045839-e484533e97d1?w=1200&q=80', caption: 'Dramatic Canyon' },
      { url: 'https://images.unsplash.com/photo-1682082040280-08b6f08878db?w=1200&q=80', caption: 'Desert Landscape' },
      { url: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=1200&q=80', caption: 'Rock Formations' },
      { url: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=1200&q=80', caption: 'Desert Sunset' }
    ],
    'Lake Districts': [
      { url: 'https://images.unsplash.com/photo-1693277675926-de9cdcce5316?w=1200&q=80', caption: 'Lake Chapala' },
      { url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', caption: 'Lakeside Villages' },
      { url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80', caption: 'Waterfront Views' },
      { url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80', caption: 'Sunset over Water' }
    ],
    'Industrial Zones': [
      { url: 'https://images.unsplash.com/photo-1648094323904-d6ae0a74838e?w=1200&q=80', caption: 'Modern Industrial Complex' },
      { url: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?w=1200&q=80', caption: 'Urban Infrastructure' },
      { url: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80', caption: 'Factory Exteriors' },
      { url: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1200&q=80', caption: 'Industrial Architecture' }
    ],
    'Historic Centers': [
      { url: 'https://images.unsplash.com/photo-1588191969960-ff8be4d184c6?w=1200&q=80', caption: 'Historic Guadalajara' },
      { url: 'https://images.unsplash.com/photo-1518638150340-f706e86654de?w=1200&q=80', caption: 'Cathedral Square' },
      { url: 'https://images.unsplash.com/photo-1709065130843-7d68eff4c0b8?w=1200&q=80', caption: 'Historic Architecture' },
      { url: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=1200&q=80', caption: 'Colonial Buildings' }
    ],
    'Rural Villages': [
      { url: 'https://images.unsplash.com/photo-1658778177301-0f71c17624ed?w=1200&q=80', caption: 'Traditional Village' },
      { url: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=1200&q=80', caption: 'Rural Community' },
      { url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', caption: 'Country Roads' },
      { url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80', caption: 'Agricultural Landscapes' }
    ]
  };

  const images = galleries[location] || [];

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-2">{location}</h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">Location Gallery</p>
              </div>
              <button 
                onClick={onClose} 
                className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] transition-colors p-2 hover:bg-[hsl(var(--bk-bg))]/50 rounded-lg"
                data-testid="close-gallery-button"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-video rounded-lg overflow-hidden group">
                  <img 
                    src={img.url} 
                    alt={img.caption}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <p className="absolute bottom-4 left-4 font-body text-sm text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    {img.caption}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
